// ACTIVE UI DICTIONARY — downloaded, cached, and shared by every screen (CCT-284).
//
// The app boots on the BUNDLED English catalogue. Choosing another language downloads that
// language's dictionary from the runtime, caches it, and applies it. This module holds that
// state; `useT` is the hook over it.
//
// ⚠️ A MODULE-LEVEL SINGLETON, ON PURPOSE. Every screen must read the SAME dictionary at the
// same moment. Per-hook state would let two screens render two languages during a switch — and
// that is exactly the class of defect this whole ticket exists to remove.
import AsyncStorage from '@react-native-async-storage/async-storage';

import {
  BUNDLED, DEFAULT_UI_LANGUAGE, isRtl, resolveUiLanguage, type Messages,
} from './index';
import { resolveApiBase } from '../config/env';

const LANG_KEY = 'ui_language';
const CACHE_PREFIX = 'ui_dict:';

type Listener = () => void;

let currentLanguage: string = DEFAULT_UI_LANGUAGE;
let currentMessages: Messages = BUNDLED;
const listeners = new Set<Listener>();

function notify(): void {
  for (const l of [...listeners]) {
    try { l(); } catch { /* one broken subscriber must not stop the others */ }
  }
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function getLanguage(): string { return currentLanguage; }
export function getMessages(): Messages { return currentMessages; }

/**
 * Fetches a dictionary from the runtime.
 *
 * ⚠️ RETURNS NULL ON ANY PROBLEM, AND NEVER THROWS. A label is not worth an unhandled rejection.
 * The caller keeps whatever dictionary it already had — English at worst — so the screen stays
 * readable when the network, the server or the database is having a bad day.
 */
async function download(language: string): Promise<Messages | null> {
  try {
    const res = await fetch(`${resolveApiBase()}/public/i18n/${encodeURIComponent(language)}`);
    if (!res.ok) return null;
    const body = await res.json();
    const messages = body?.messages;
    // A well-formed but EMPTY answer is a failure, not a dictionary: adopting it would blank
    // every label the bundle could otherwise have covered.
    if (!messages || typeof messages !== 'object' || !Object.keys(messages).length) return null;
    return messages as Messages;
  } catch {
    return null;
  }
}

async function readCache(language: string): Promise<Messages | null> {
  try {
    const raw = await AsyncStorage.getItem(CACHE_PREFIX + language);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && Object.keys(parsed).length ? (parsed as Messages) : null;
  } catch {
    return null;
  }
}

async function writeCache(language: string, messages: Messages): Promise<void> {
  try {
    await AsyncStorage.setItem(CACHE_PREFIX + language, JSON.stringify(messages));
  } catch { /* a full or unavailable store only costs a re-download */ }
}

/**
 * Applies a language: cache first so the screen turns immediately, network after so it is fresh.
 *
 * ⚠️ ENGLISH IS NEVER DOWNLOADED. It ships in the bundle, so switching back to it is instant and
 * works offline — including when someone picked a language they cannot read and needs a way out.
 */
export async function setLanguage(value: unknown): Promise<string> {
  const next = resolveUiLanguage(value, null);
  currentLanguage = next;

  if (next === DEFAULT_UI_LANGUAGE) {
    currentMessages = BUNDLED;
    notify();
  } else {
    const cached = await readCache(next);
    if (cached) { currentMessages = cached; notify(); }
    const fresh = await download(next);
    if (fresh) {
      currentMessages = fresh;
      notify();
      void writeCache(next, fresh);
    } else if (!cached) {
      // Nothing cached and nothing downloaded: stay on English rather than show raw keys.
      currentMessages = BUNDLED;
      notify();
    }
  }

  applyDirection(next);
  try { await AsyncStorage.setItem(LANG_KEY, next); } catch { /* best-effort */ }
  return next;
}

/**
 * Boot: adopt the stored language (or the `?lang=` parameter), then load its dictionary.
 *
 * The parameter wins over the stored preference, on purpose: `?lang=ar` must show Arabic even to
 * someone who once chose French — otherwise a link shared to have a translation reviewed renders
 * in the reviewer's own language and the review is worthless.
 */
export async function boot(param?: string | null): Promise<string> {
  let stored: string | null = null;
  try { stored = await AsyncStorage.getItem(LANG_KEY); } catch { /* English does the job */ }
  return setLanguage(resolveUiLanguage(param, stored));
}

/**
 * Applies the writing direction of a language to the document.
 *
 * ⚠️ WEB ONLY, AND DELIBERATELY SO. React Native's `I18nManager.forceRTL` needs a full app
 * RESTART to take effect — calling it here would flip a flag that only applies next launch,
 * which reads to the user as "the setting did nothing". On the web, `dir` on the root element
 * takes effect immediately, and that is the platform this ships on. The native switch belongs
 * with the native release, where a relaunch can be part of the flow.
 */
export function applyDirection(language: string): void {
  try {
    if (typeof document === 'undefined') return;
    const dir = isRtl(language) ? 'rtl' : 'ltr';
    document.documentElement.setAttribute('dir', dir);
    document.documentElement.setAttribute('lang', language);
  } catch { /* never let a layout attribute break rendering */ }
}

/** Test seam: restore the module to its boot state. */
export function resetForTests(): void {
  currentLanguage = DEFAULT_UI_LANGUAGE;
  currentMessages = BUNDLED;
  listeners.clear();
}
